import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { RoomOptionCardProps } from '../types/bot.types';
import { Button } from 'antd';
import { IoBed } from 'react-icons/io5';
import { formatBotPrice } from './formatBotPrice';
import { formatHotelRoomAmenityFallback } from './formatHotelRoomOption';

/**
 * One pricing composition (a single book_hash) rendered as a card. Mirrors the
 * 3-part HotelResultCard layout (image / body / footer) so the room list reads
 * as a natural continuation of the hotel cards. Selecting fires onSelect, which
 * sends the friendly bubble text + structured book_hash (see RoomOptionsList).
 */
const RoomOptionCard: React.FC<RoomOptionCardProps> = ({
  roomName,
  board,
  totalPrice,
  pricePerNight,
  currency,
  freeCancellation,
  guests,
  cancellationSummary,
  amenityKeys,
  imageUrl,
  onSelect,
}) => {
  const t = useSelector((s: RootState) => (s.config.translations as any)?.bot);
  const hotelAmenitiesT = useSelector(
    (s: RootState) => (s.config.translations as any)?.hotelAmenities ?? {},
  );

  return (
    <div className="bot-result-card bot-result-card--room">
      <div className="bot-result-card__image-wrap">
        {imageUrl ? (
          <img src={imageUrl} alt={roomName} className="bot-result-card__image" />
        ) : (
          <div className="bot-result-card__image-placeholder">
            <IoBed size={32} />
          </div>
        )}
      </div>
      <div className="bot-result-card__body">
        <div className="bot-result-card__name">{roomName}</div>
        <div className="bot-result-card__badges">
          {board && <span className="bot-result-card__badge">{board}</span>}
          {freeCancellation ? (
            <span className="bot-result-card__badge bot-result-card__badge--green">
              {t?.result?.freeCancellation ?? 'Free cancellation'}
            </span>
          ) : (
            <span className="bot-result-card__badge bot-result-card__badge--muted">
              {t?.result?.nonRefundable ?? 'Non-refundable'}
            </span>
          )}
        </div>
        {guests && (
          <div className="bot-result-card__meta bot-result-card__guests">
            {guests}
          </div>
        )}
        {cancellationSummary && (
          <div className="bot-result-card__meta bot-result-card__cancellation">
            {cancellationSummary}
          </div>
        )}
        {!!amenityKeys?.length && (
          <div className="bot-result-card__amenities">
            {amenityKeys.map((key) => (
              <span key={key} className="bot-result-card__amenity">
                {hotelAmenitiesT[key] ?? formatHotelRoomAmenityFallback(key)}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="bot-result-card__footer">
        <div>
          {pricePerNight !== undefined && (
            <div className="bot-result-card__per-night">
              {currency} {formatBotPrice(pricePerNight)}
              {t?.result?.perNight ?? '/night'}
            </div>
          )}
          <div className="bot-result-card__price">
            {currency} {formatBotPrice(totalPrice)}
          </div>
        </div>
        <Button size="small" type="primary" className="btn-style-5" onClick={onSelect}>
          {t?.result?.select ?? 'Select'}
        </Button>
      </div>
    </div>
  );
};

export default RoomOptionCard;
